// submissionApi.ts — API utilities for community campsite submissions

import type { CampsiteSubmission, SubmissionImage, Feature, Region } from '../types';

// API base
const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au/api';

// Get auth headers if reviewer is logged in
function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('reviewer_token') : null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// Submit a new campsite
export async function submitCampsiteSubmission(data: Record<string, unknown>): Promise<CampsiteSubmission> {
  const res = await fetch(`${API_BASE}/submissions`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Submission failed'); }
  return res.json();
}

// Get presigned S3 upload URL for submission image
export async function getSubmissionUploadUrl(submissionId: string, filename: string, contentType: string): Promise<{upload_url: string; image_src: string}> {
  const res = await fetch(`${API_BASE}/submissions/${submissionId}/images/upload-url`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({filename, content_type: contentType}) });
  if (!res.ok) throw new Error('Failed to get upload URL');
  return res.json();
}

// Register an uploaded image on a submission
export async function registerSubmissionImage(submissionId: string, imageData: Record<string, unknown>): Promise<SubmissionImage> {
  const res = await fetch(`${API_BASE}/submissions/${submissionId}/images`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(imageData) });
  if (!res.ok) throw new Error('Failed to register image');
  return res.json();
}

// Fetch reviewer's own submissions
export async function fetchMySubmissions(): Promise<CampsiteSubmission[]> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('reviewer_token') : null;
  if (!token) return [];
  const res = await fetch(`${API_BASE}/submissions/mine`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return [];
  const data = await res.json();
  return data.submissions || [];
}

// Fetch all features for the form
export async function fetchAllFeatures(): Promise<Feature[]> {
  const res = await fetch(`${API_BASE}/entities/features`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.features || data || [];
}

// Fetch all regions for the form
export async function fetchAllRegions(): Promise<Region[]> {
  const res = await fetch(`${API_BASE}/regions`);
  if (!res.ok) return [];
  const data = await res.json();
  return data.regions || data || [];
}

// Pending submission storage (for progressive auth - same pattern as pendingReview)
const PENDING_SUBMISSION_KEY = 'pendingSubmission';

export function savePendingSubmission(data: Record<string, unknown>): void {
  if (typeof window !== 'undefined') sessionStorage.setItem(PENDING_SUBMISSION_KEY, JSON.stringify(data));
}

export function getPendingSubmission(): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_SUBMISSION_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function clearPendingSubmission(): void {
  if (typeof window !== 'undefined') sessionStorage.removeItem(PENDING_SUBMISSION_KEY);
}
